import './styles.css';

import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';

// Confirms the OTA bundle booted — runs first, before any other bootstrap or
// network/chunk fetch. If the native layer doesn't hear this within
// appReadyTimeout it rolls back to the previous bundle.
//
// For local builds (VITE_LOCAL_BUILD=true): evict any cached OTA bundle so the
// freshly-built native bundle is always used instead.
if (Capacitor.isNativePlatform()) {
  if ((import.meta as { env?: Record<string, string> }).env?.VITE_LOCAL_BUILD === 'true') {
    CapacitorUpdater.current()
      .then(({ bundle }) => {
        if (bundle.id !== 'builtin') {
          // Reload to the built-in bundle; the WebView restarts and won't reach
          // here. If it doesn't, the app is running fine on the current bundle
          // and still owes the native layer a readiness signal — without it the
          // updater times out and rolls back a healthy app.
          CapacitorUpdater.reset({ toLastSuccessful: false }).catch((err) => {
            console.error('[TimeHuddle] OTA reset failed:', err);
            CapacitorUpdater.notifyAppReady().catch(() => {});
          });
        } else {
          CapacitorUpdater.notifyAppReady().catch((err) =>
            console.error('[TimeHuddle] notifyAppReady failed:', err),
          );
        }
      })
      .catch(() => {
        CapacitorUpdater.notifyAppReady().catch(() => {});
      });
  } else {
    CapacitorUpdater.notifyAppReady().catch((err) =>
      console.error('[TimeHuddle] notifyAppReady failed:', err),
    );
  }
}

// ─── Eager theme + brand bootstrap ───────────────────────────────────────────
// Apply theme immediately (before React mounts) so login, landing, and all
// pre-auth pages receive the correct data-theme / .dark class without flicker.
(function applyBootstrapTheme() {
  const stored = localStorage.getItem('app:theme');
  const theme = stored === 'light' || stored === 'dark' ? stored : 'dark';
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.classList.toggle('dark', theme === 'dark');
})();

// Apply brand CSS eagerly so login/signup pages get the right brand colors.
// useBrand() only runs inside AppLayout, so pre-auth pages need this bootstrap.
(function applyBootstrapBrand() {
  const BUILD_TIME_BRAND = 'bluehive';
  const DEFAULT_BRAND = 'webchart';
  const stored = localStorage.getItem('app:brand');
  const brand = stored || DEFAULT_BRAND;
  if (brand === BUILD_TIME_BRAND) return; // baked into CSS, no injection needed
  // Dynamically import brand and inject CSS — async but fast enough for pre-auth pages
  import('@mieweb/ui/brands').then(({ generateBrandCSS, brands }) => {
    const loader = brands[brand as keyof typeof brands] ?? brands[DEFAULT_BRAND];
    loader().then((config) => {
      const el = document.createElement('style');
      el.id = 'mieweb-brand-override';
      el.textContent = generateBrandCSS(config);
      document.head.appendChild(el);
    });
  });
})();

// ─── Startup timing (visible in Xcode device console) ─────────────────────────
const t0 = performance.now();
const _log = (msg: string) =>
  console.log(`[TimeHuddle] +${(performance.now() - t0).toFixed(0)}ms ${msg}`);
_log('main.tsx evaluated');

import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import React, { lazy, Suspense } from 'react';
import { Spinner } from '@mieweb/ui';
import { createRoot } from 'react-dom/client';

// Debug: check Capacitor bridge detection
_log(
  `Capacitor.isNativePlatform()=${Capacitor.isNativePlatform()}, platform=${Capacitor.getPlatform()}`,
);
_log(`window.webkit?.messageHandlers?.bridge=${!!(window as any).webkit?.messageHandlers?.bridge}`);
_log(`window.Capacitor=${JSON.stringify(Object.keys((window as any).Capacitor || {}))}`);

import { InboxPage } from './features/inbox/InboxPage';
import { PublicReleaseNotesPage } from './features/release-notes/PublicReleaseNotesPage';
import { WatchPage, watchPathVideoId } from './features/pulse-upload/WatchPage';
import { enterpriseApi } from './lib/api';
import { markTappedNotificationRead } from './features/notifications/notificationEvents';
import { getDdpClient, subscribeNewNotifications } from './lib/ddp';
import { autoRegisterPush, checkPushNotificationStatus } from './lib/nativePush';
import { SessionProvider, useSession } from './lib/useSession';
import { rememberReturnTo, restoreReturnTo } from './lib/returnTo';
import { redirectSignedInRootToDashboard } from './lib/rootRedirect';
import { InstallerModal } from './ui/InstallerModal';
import { LandingPage } from './ui/LandingPage';
import { LoginForm } from './ui/LoginForm';
import { OtaUpdateGate } from './ui/OtaUpdateGate';
import { UsernameClaimModal } from './ui/UsernameClaimModal';

// The signed-in shell is its own chunk: the login form and landing page paint
// without first downloading every app page.
const loadAppLayout = () => import('./ui/AppLayout').then((m) => ({ default: m.AppLayout }));
const AppLayout = lazy(loadAppLayout);

// A deploy replaces dist/, so a tab opened before it can ask for a page chunk
// whose hash no longer exists. Reload to pick up the new build instead of failing.
window.addEventListener('vite:preloadError', () => window.location.reload());

// ─── Android back gesture/button ──────────────────────────────────────────────
//
// A backButton listener replaces Capacitor's default handling, so step back
// through in-app history here and send the app to the background (rather than
// closing it) once there is nothing left to go back to. iOS has no such event;
// its edge swipe is enabled natively in MainViewController.
if (Capacitor.getPlatform() === 'android') {
  void CapApp.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) window.history.back();
    else void CapApp.minimizeApp();
  });
}

// ─── Dead-socket check on resume ─────────────────────────────────────────────
//
// The live socket can die while the app is backgrounded without ever closing, so
// check it on return; otherwise the first call after resume waits on it.
const verifyLiveConnection = () => void getDdpClient().verifyConnection();
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') verifyLiveConnection();
});
if (Capacitor.isNativePlatform()) {
  void CapApp.addListener('appStateChange', ({ isActive }) => {
    if (isActive) verifyLiveConnection();
  });
}

// ─── Deep link handling (Capacitor native only) ───────────────────────────────
//
// Password reset emails contain a timehuddle://reset?token=XXX link.
// When the user taps it, the OS opens the app and fires appUrlOpen.
// We store the token in this module-level variable so the App component
// can read it on mount (and on every resume).

let _deepLinkToken: string | null = null;

// Pending team/org join context from an OAuth deep link (?join=/?invite=/
// ?org_invite= carried through the native redirect-away-and-back flow, since
// the URL bar isn't available to read query params from on native). Applied
// once the App component sees an authenticated session.
let _pendingOAuthJoin: { join?: string; invite?: string; orgInvite?: string } | null = null;

if (Capacitor.isNativePlatform()) {
  void CapApp.addListener('appUrlOpen', ({ url }) => {
    try {
      const parsed = new URL(url);

      // OAuth callback: timehuddle://auth?meteor_token=...&meteor_resume=...
      if (parsed.host === 'auth') {
        const meteorResume = parsed.searchParams.get('meteor_resume');
        const join = parsed.searchParams.get('join');
        const invite = parsed.searchParams.get('invite');
        const orgInvite = parsed.searchParams.get('org_invite');
        if (join || invite || orgInvite) {
          _pendingOAuthJoin = {
            join: join ?? undefined,
            invite: invite ?? undefined,
            orgInvite: orgInvite ?? undefined,
          };
        }
        // Close the in-app browser and return to the app.
        void Browser.close();
        if (meteorResume) {
          localStorage.setItem('meteor_resume_token', meteorResume);
          // Notify SessionProvider to log the DDP connection in with the new
          // token and refetch — a bare renderRoot() won't re-authenticate.
          window.dispatchEvent(
            new CustomEvent('timehuddle:oauth-login', { detail: { resumeToken: meteorResume } }),
          );
        }
        return;
      }

      // Generic in-app deep link: timehuddle://open/app/profile/xyz?tab=work
      // Used for links that aren't push-notification taps (e.g. shared links).
      // Stashed for cold start (JS bridge/router not mounted yet) and also
      // dispatched live for the case the app is already running.
      if (parsed.host === 'open') {
        // The path comes from outside the app, so only in-app routes are
        // accepted: `//example.com` is a scheme-relative URL that pushState
        // rejects cross-origin, and it would stay persisted for the next launch.
        if (!/^\/app(\/|$)/.test(parsed.pathname)) return;
        const target = `${parsed.pathname}${parsed.search}`;
        try {
          localStorage.setItem('pendingDeepLinkPath', target);
        } catch {
          /* ignore */
        }
        window.dispatchEvent(new CustomEvent('timehuddle:deeplink', { detail: { path: target } }));
        return;
      }

      // Password reset: timehuddle://reset?token=XXX
      const token = parsed.searchParams.get('token');
      if (token) {
        _deepLinkToken = token;
        // Re-render the root so the App component picks up the token.
        renderRoot();
      }
    } catch {
      // Malformed URL — ignore
    }
  });
}

// ─── OAuth callback handler (synchronous, before session check) ───────────────
// Handle OAuth callback tokens synchronously before session check runs.
// Stores meteor_resume token in localStorage so tryResumeLogin() picks it up.
(function handleOAuthCallback() {
  const params = new URLSearchParams(window.location.search);
  const meteorResume = params.get('meteor_resume');
  if (meteorResume) {
    localStorage.setItem('meteor_resume_token', meteorResume);
    // Clean URL
    const url = new URL(window.location.href);
    url.searchParams.delete('meteor_token');
    url.searchParams.delete('meteor_resume');
    window.history.replaceState({}, '', url.toString());
  }
})();

// ─── App (client-side rendered, /app and all non-root routes) ─────────────────
_log('App component defined — modules loaded');

const App: React.FC = () => {
  const { user, loading, needsUsernameClaim, refetch } = useSession();
  // Once-per-user effects key on the id: `user` is a new object after every
  // session refetch (e.g. marking release notes read), which would rerun them.
  const userId = user?.id ?? null;
  const [ownershipChecked, setOwnershipChecked] = React.useState(false);
  const [showTakeOwnershipModal, setShowTakeOwnershipModal] = React.useState(false);

  // Auto-register push on native (APNs/FCM) and web (VAPID) after login.
  React.useEffect(() => {
    if (userId) void autoRegisterPush(userId);
  }, [userId]);

  // Apply a pending team/org join from a social sign-in (?join=/?invite=/
  // ?org_invite=). Social sign-in redirects away to the IdP and back, so it
  // never runs LoginForm's password-flow `acceptInvitation()` — this is the
  // equivalent for OAuth, reading the params from the URL (web) or the
  // `timehuddle://auth` deep link (native).
  React.useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const join = params.get('join') ?? _pendingOAuthJoin?.join ?? null;
    const invite = params.get('invite') ?? _pendingOAuthJoin?.invite ?? null;
    const orgInvite = params.get('org_invite') ?? _pendingOAuthJoin?.orgInvite ?? null;
    if (!join && !invite && !orgInvite) return;
    _pendingOAuthJoin = null;

    void (async () => {
      const ddp = getDdpClient();
      try {
        if (invite) await ddp.acceptTeamInvitation(invite);
        if (orgInvite) await ddp.acceptOrgInvitation(orgInvite);
        if (join) await ddp.joinTeamByQrCode(join);
      } catch (err) {
        console.error('[oauth] pending team/org join failed:', err);
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('join');
      url.searchParams.delete('invite');
      url.searchParams.delete('org_invite');
      window.history.replaceState(null, '', url.toString());
      await refetch();
    })();
  }, [user, refetch]);

  // SSE fallback: show a browser Notification for every incoming SSE event.
  // This fires even when FCM/VAPID push delivery is unreliable (e.g. localhost).
  // Skipped on native Capacitor (APNs handles it), when permission not granted,
  // or when the user has not opted in to push notifications (unsubscribed).
  React.useEffect(() => {
    if (!userId || Capacitor.isNativePlatform()) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    void checkPushNotificationStatus().then((status) => {
      if (cancelled || !status.subscribed) return;
      unsubscribe = subscribeNewNotifications((n) => {
        // Only what the user's preferences let pop up on desktop (#784): an
        // event set to Phone only, or held by quiet time, stays in the inbox.
        if (n.pushed?.desktop === false) return;
        const nData = n.data;
        const notif = new Notification(n.title, {
          body: n.body,
          icon: '/logo.png',
          // Same tag as the web push for this row, so the two collapse into one.
          tag: n.id,
          silent: false,
        });
        if (nData) {
          notif.onclick = () => {
            window.focus();
            markTappedNotificationRead({ notificationId: n.id, type: nData.type });
            const url = nData.url as string | undefined;
            if (url?.startsWith('/app/')) {
              // Keep the query string — it carries the deep-link target
              // (post, team, profile tab, channel).
              window.history.pushState(null, '', url);
              window.dispatchEvent(new PopStateEvent('popstate'));
            }
          };
        }
      });
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [userId]);

  React.useEffect(() => {
    if (!userId || needsUsernameClaim) {
      setOwnershipChecked(false);
      setShowTakeOwnershipModal(false);
      return;
    }

    let cancelled = false;
    void enterpriseApi
      .getOwnershipStatus()
      .then((status) => {
        if (cancelled) return;
        setShowTakeOwnershipModal(!status.installCompleted && !status.hasOwner);
      })
      .catch(() => {
        if (cancelled) return;
        setShowTakeOwnershipModal(false);
      })
      .finally(() => {
        if (cancelled) return;
        setOwnershipChecked(true);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, needsUsernameClaim]);

  // While signed out, fetch the shell chunk in idle time so signing in doesn't
  // wait on it after the credentials round trip.
  React.useEffect(() => {
    if (user || loading) return;
    const idle = window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1));
    idle(() => void loadAppLayout());
  }, [user, loading]);

  // Reset token: check URL params (web) or deep link (native).
  const resetToken =
    _deepLinkToken ??
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('token')
      : null);

  if (resetToken) {
    return <LoginForm initialMode="reset-confirm" />;
  }

  // Wait for initial session check to complete before showing login
  // (but allow app to render during refetch when user is already known)
  if (loading && !user) {
    return null;
  }

  if (!user) {
    // Ensure we're on root path when showing login — fixes issue where
    // logout from /app/teams would show login form but keep /app/teams URL.
    // The link is kept so signing in returns to it (see lib/returnTo).
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/app/')) {
      rememberReturnTo();
      window.history.replaceState(null, '', '/');
    }
    return <LoginForm />;
  }

  // First signed-in render after a deep link: put the URL back before
  // AppLayout's router reads it.
  restoreReturnTo();

  // /login only renders the sign-in form; once signed in it isn't an app route,
  // so land on the app instead of the "page doesn't exist" screen. The query
  // string is kept — it may carry the OAuth authorize params read just below.
  if (typeof window !== 'undefined' && window.location.pathname === '/login') {
    window.history.replaceState(null, '', '/app' + window.location.search + window.location.hash);
  }
  // If the user is already authenticated and there are OAuth 2.0 authorization
  // params in the URL (e.g. redirected here from TimeHarbor), forward them
  // back to Better Auth's authorization endpoint so it can issue the code.
  const oauthParams =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  if (
    oauthParams?.get('response_type') &&
    oauthParams?.get('client_id') &&
    oauthParams?.get('state')
  ) {
    // The app uses Bearer token (localStorage) for API calls, but the OIDC /authorize
    // endpoint is a browser navigation — it can't send custom headers. Copy the token
    // into a cookie so Better Auth can recognise the session during the authorize flow.
    const token = localStorage.getItem('timecore_session_token');
    if (token) {
      document.cookie = `better-auth.session_token=${token}; path=/; SameSite=Lax`;
    }
    window.location.href = `/api/auth/oauth2/authorize?${oauthParams.toString()}`;
    return null;
  }
  // Keyed by user: the shell keeps a lot mounted (the Tickets page, a ticket
  // start waiting on a clock-in), and a session can change hands without passing
  // through signed-out — another tab signs in, then this one's socket reconnects.
  // A new key remounts the shell, so nothing one user left behind reaches the next.
  const shell = (
    <Suspense
      fallback={
        <div
          className="shell-loading flex h-dvh items-center justify-center"
          aria-live="polite"
          aria-busy="true"
        >
          <Spinner />
          <span className="sr-only">Loading TimeHuddle</span>
        </div>
      }
    >
      <AppLayout key={user.id} />
    </Suspense>
  );
  if (needsUsernameClaim)
    return (
      <>
        {shell}
        <UsernameClaimModal />
      </>
    );

  return (
    <>
      {shell}
      {ownershipChecked && showTakeOwnershipModal && (
        <InstallerModal onTaken={() => setShowTakeOwnershipModal(false)} />
      )}
    </>
  );
};

// ─── Entry point ──────────────────────────────────────────────────────────────

let _root: ReturnType<typeof createRoot> | null = null;

function renderRoot() {
  _log('renderRoot called');
  const el = document.getElementById('root');
  if (!el) return;

  if (!_root) {
    if (window.location.pathname === '/') {
      // On native (Capacitor iOS/Android) the WebView always starts at '/'.
      // Skip the marketing landing page and go straight to the app.
      if (Capacitor.isNativePlatform()) {
        _log('native platform detected — mounting SessionProvider + App');
        _root = createRoot(el);
        _root.render(
          <OtaUpdateGate>
            <SessionProvider>
              <App />
            </SessionProvider>
          </OtaUpdateGate>,
        );
        return;
      }
      // Signed in: replace the URL with the dashboard and mount the app below,
      // so the landing page never paints.
      if (!redirectSignedInRootToDashboard()) {
        _root = createRoot(el);
        _root.render(<LandingPage />);
        return;
      }
    } else if (window.location.pathname === '/inbox') {
      _root = createRoot(el);
      _root.render(<InboxPage />);
      return;
    } else if (watchPathVideoId(window.location.pathname)) {
      // A video's own page: public, since a shared link reaches people who
      // aren't signed in (see WatchPage).
      _root = createRoot(el);
      _root.render(<WatchPage videoId={watchPathVideoId(window.location.pathname)!} />);
      return;
    } else if (window.location.pathname === '/release-notes') {
      // Public on purpose: linked from the landing page, so someone deciding
      // whether to sign up can read what shipped without an account.
      _root = createRoot(el);
      _root.render(<PublicReleaseNotesPage />);
      return;
    }

    _root = createRoot(el);
  }

  _root.render(
    <OtaUpdateGate>
      <SessionProvider>
        <App />
      </SessionProvider>
    </OtaUpdateGate>,
  );
}

renderRoot();
